// AbilityEditor.tsx
import React, { useCallback, useMemo } from "react";
import { Form, Button, Row, Col, Badge } from "react-bootstrap";
import {
    AbilityTypeList, AbilityTypes, CardPropertyOptions,
    controllerOptionsList, ControllerType, EffectType,
    EffectTypeList, MathSign, MathSignsList, ReadableProperty,
    TargetTypes, targetTypesList, TriggerType, TriggerTypeList,
    Zone, zoneOptionsList
} from "../../util/cardEffects/effectTypes.ts";
import { ABILITY_FIELDS } from "./cardManagementPanel.tsx";

interface ValueEditorProps {
    value: any;
    onChange: (value: any) => void;
    disabled?: boolean;
}

// Recursive value editor for the complex Value union type
const ValueEditor: React.FC<ValueEditorProps> = ({
    value,
    onChange,
    disabled,
}) => {
    const valueType = value?.type;

    const setType = useCallback(
        (type: string | null) => {
            if (type === null) {
                onChange(undefined);
                return;
            }
            switch (type) {
                case "number":
                    onChange(0);
                    break;
                case "target-count":
                    onChange({ type: "target-count", target: { type: "player" } });
                    break;
                case "ritual-value":
                    onChange({ type: "ritual-value" });
                    break;
                case "modifier-value":
                    onChange({ type: "modifier-value" });
                    break;
                case "stored-value":
                    onChange({ type: "stored-value", key: "" });
                    break;
                case "math":
                    onChange({ type: "math", sign: "+", left: 0, right: 0 });
                    break;
                case "property":
                    onChange({ type: "property", property: "cost", cardId: "this" });
                    break;
                case "user":
                    onChange({ type: "user", max: 10, min: 0 });
                    break;
                default:
                    onChange(undefined);
            }
        },
        [onChange]
    );

    const renderValueFields = () => {
        if (typeof value === "number") {
            return (
                <Form.Control
                    type="number"
                    value={value}
                    onChange={(e) => onChange(Number(e.target.value))}
                    disabled={disabled}
                    size="sm"
                />
            );
        }

        switch (valueType) {
            case "target-count":
                return (
                    <div className="border-start ps-2 ms-2">
                        <small className="text-muted">Target:</small>
                        <TargetEditor
                            value={value.target}
                            onChange={(t) => onChange({ ...value, target: t })}
                            disabled={disabled}
                        />
                    </div>
                );

            case "stored-value":
                return (
                    <Form.Control
                        size="sm"
                        value={value.key}
                        onChange={(e) => onChange({ ...value, key: e.target.value })}
                        placeholder="Key name"
                        disabled={disabled}
                    />
                );

            case "math":
                return (
                    <div className="border-start ps-2 ms-2">
                        <Row className="g-2">
                            <Col xs="auto">
                                <Form.Select
                                    size="sm"
                                    value={value.sign}
                                    onChange={(e) =>
                                        onChange({ ...value, sign: e.target.value as MathSign })
                                    }
                                    disabled={disabled}
                                >
                                    {MathSignsList.map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                        </Row>
                        <Row className="g-2 mt-1">
                            <Col>
                                <small className="text-muted">Left:</small>
                                <ValueEditor
                                    value={value.left}
                                    onChange={(v) => onChange({ ...value, left: v })}
                                    disabled={disabled}
                                />
                            </Col>
                            <Col>
                                <small className="text-muted">Right:</small>
                                <ValueEditor
                                    value={value.right}
                                    onChange={(v) => onChange({ ...value, right: v })}
                                    disabled={disabled}
                                />
                            </Col>
                        </Row>
                    </div>
                );

            case "property":
                return (
                    <div className="border-start ps-2 ms-2">
                        <Row className="g-2">
                            <Col>
                                <Form.Select
                                    size="sm"
                                    value={value.property}
                                    onChange={(e) =>
                                        onChange({
                                            ...value,
                                            property: e.target.value as ReadableProperty,
                                        })
                                    }
                                    disabled={disabled}
                                >
                                    {CardPropertyOptions.map((p) => (
                                        <option key={p} value={p}>
                                            {p}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col>
                                <Form.Select
                                    size="sm"
                                    value={
                                        typeof value.cardId === "string" ? value.cardId : "stored"
                                    }
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === "stored") {
                                            onChange({
                                                ...value,
                                                cardId: { type: "stored-card", key: "" },
                                            });
                                        } else {
                                            onChange({ ...value, cardId: val });
                                        }
                                    }}
                                    disabled={disabled}
                                >
                                    <option value="this">this</option>
                                    <option value="stored">stored-card</option>
                                </Form.Select>
                            </Col>
                        </Row>
                        {typeof value.cardId !== "string" && (
                            <Form.Control
                                size="sm"
                                className="mt-1"
                                value={value.cardId.key}
                                onChange={(e) =>
                                    onChange({
                                        ...value,
                                        cardId: { ...value.cardId, key: e.target.value },
                                    })
                                }
                                placeholder="Stored card key"
                                disabled={disabled}
                            />
                        )}
                    </div>
                );

            case "user":
                return (
                    <div className="border-start ps-2 ms-2">
                        <Row className="g-2">
                            <Col>
                                <small className="text-muted">Min:</small>
                                <ValueEditor
                                    value={value.min}
                                    onChange={(v) => onChange({ ...value, min: v })}
                                    disabled={disabled}
                                />
                            </Col>
                            <Col>
                                <small className="text-muted">Max:</small>
                                <ValueEditor
                                    value={value.max}
                                    onChange={(v) => onChange({ ...value, max: v })}
                                    disabled={disabled}
                                />
                            </Col>
                        </Row>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="mb-2">
            <Form.Select
                size="sm"
                value={
                    typeof value === "number"
                        ? "number"
                        : valueType || ""
                }
                onChange={(e) =>
                    setType(e.target.value === "" ? null : e.target.value)
                }
                disabled={disabled}
                className="mb-1"
            >
                <option value="">Select value type...</option>
                <option value="number">Number</option>
                <option value="target-count">Target Count</option>
                <option value="modifier-value">Modifier Value</option>
                <option value="stored-value">Stored Value</option>
                <option value="math">Math Operation</option>
                <option value="property">Card Property</option>
                <option value="user">User Input</option>
                <option value="ritual-value">Ritual Intensity</option>
            </Form.Select>
            {renderValueFields()}
        </div>
    );
};

// Target editor component
interface TargetEditorProps {
    value: any;
    onChange: (value: any) => void;
    disabled?: boolean;
    showCount?: boolean;
}

const TargetEditor: React.FC<TargetEditorProps> = ({
    value,
    onChange,
    disabled,
    showCount = true,
}) => {
    const updateField = useCallback(
        (field: string, val: any) => {
            onChange({ ...value, [field]: val });
        },
        [value, onChange]
    );

    const removeField = useCallback(
        (field: string) => {
            const { [field]: _, ...rest } = value;
            onChange(rest);
        },
        [value, onChange]
    );

    const setType = useCallback(
        (newType: TargetTypes | "stored-card") => {
            if (newType === "stored-card") {
                onChange({ type: "stored-card", key: value?.key ?? "" });
            } else {
                const { key: _, ...rest } = value;
                onChange({ ...rest, type: newType });
            }
        },
        [value, onChange]
    );

    if (value.type === "stored-card") {
        return (
            <div className="border rounded p-2 bg-light">
                <Row className="g-2">
                    <Col xs={6}>
                        <Form.Select
                            size="sm"
                            value="stored-card"
                            onChange={(e) => setType(e.target.value as TargetTypes | "stored-card")}
                            disabled={disabled}
                        >
                            <option value="stored-card">stored-card</option>
                            {targetTypesList.map((t) => (
                                <option key={t} value={t}>
                                    {t}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>
                </Row>
                <Row className="g-2 mt-2">
                    <Col xs={6}>
                        <Form.Control
                            size="sm"
                            type="text"
                            value={value?.key ?? ""}
                            onChange={(e) => {
                                const val = e.target.value;
                                updateField("key", val);
                            }}
                            placeholder="Count (number or 'all')"
                            disabled={disabled}
                        />
                    </Col>
                </Row>
            </div>
        );
    }

    return (
        <div className="border rounded p-2 bg-light">
            <Row className="g-2">
                <Col xs={6}>
                    <Form.Select
                        size="sm"
                        value={value?.type || "player"}
                        onChange={(e) => setType(e.target.value as TargetTypes | "stored-card")}
                        disabled={disabled}
                    >
                        {targetTypesList.map((t) => (
                            <option key={t} value={t}>
                                {t}
                            </option>
                        ))}
                        <option value="stored-card">stored-card</option>
                    </Form.Select>
                </Col>
                <Col xs={6}>
                    <Form.Select
                        size="sm"
                        value={value?.controller || ""}
                        onChange={(e) =>
                            updateField(
                                "controller",
                                e.target.value === "" ? undefined : (e.target.value as ControllerType)
                            )
                        }
                        disabled={disabled}
                    >
                        <option value="">Any controller</option>
                        {controllerOptionsList.map((c) => (
                            <option key={c} value={c}>
                                {c}
                            </option>
                        ))}
                    </Form.Select>
                </Col>
            </Row>

            <Row className="g-2 mt-2">
                <Col xs={6}>
                    <Form.Select
                        size="sm"
                        value={value?.zone || ""}
                        onChange={(e) =>
                            updateField(
                                "zone",
                                e.target.value === "" ? undefined : (e.target.value as Zone)
                            )
                        }
                        disabled={disabled}
                    >
                        <option value="">Any zone</option>
                        {zoneOptionsList.map((z) => (
                            <option key={z} value={z}>
                                {z}
                            </option>
                        ))}
                    </Form.Select>
                </Col>
                {showCount && (
                    <Col xs={6}>
                        <Form.Control
                            size="sm"
                            type="text"
                            value={value?.count ?? ""}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === "all") {
                                    updateField("count", "all");
                                } else if (val === "") {
                                    removeField("count");
                                } else {
                                    const num = parseInt(val);
                                    if (!isNaN(num)) updateField("count", num);
                                }
                            }}
                            placeholder="Count (number or 'all')"
                            disabled={disabled}
                        />
                    </Col>
                )}
            </Row>

            <Row className="g-2 mt-2">
                <Col xs={6}>
                    <Form.Select
                        size="sm"
                        value={value?.id === "this" ? "this" : value?.id ? "value" : ""}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (val === "") removeField("id");
                            else if (val === "this") updateField("id", "this");
                            else updateField("id", 0);
                        }}
                        disabled={disabled}
                    >
                        <option value="">Any ID</option>
                        <option value="this">this card</option>
                        <option value="value">Value...</option>
                    </Form.Select>
                    {value?.id !== undefined && value?.id !== "this" && (
                        <ValueEditor
                            value={value.id}
                            onChange={(v) => updateField("id", v)}
                            disabled={disabled}
                        />
                    )}
                </Col>
                <Col xs={6}>
                    <Form.Select
                        size="sm"
                        value={value?.idNot === "this" ? "this" : value?.idNot ? "value" : ""}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (val === "") removeField("idNot");
                            else if (val === "this") updateField("idNot", "this");
                            else updateField("idNot", 0);
                        }}
                        disabled={disabled}
                    >
                        <option value="">Not excluded</option>
                        <option value="this">not this card</option>
                        <option value="value">not Value...</option>
                    </Form.Select>
                    {value?.idNot !== undefined && value?.idNot !== "this" && (
                        <ValueEditor
                            value={value.idNot}
                            onChange={(v) => updateField("idNot", v)}
                            disabled={disabled}
                        />
                    )}
                </Col>
            </Row>
        </div>
    );
};

// Main effect editor
interface EffectEditorProps {
    passive?: boolean;
    effect: any;
    onChange: (effect: any) => void;
    onRemove?: () => void;
    disabled?: boolean;
    depth?: number;
}

const EffectEditor: React.FC<EffectEditorProps> = ({
    passive = false,
    effect,
    onChange,
    onRemove,
    disabled,
    depth = 0,
}) => {
    const effectType = effect?.type;

    const setEffectType = useCallback(
        (type: EffectType | "multiple" | "iterated" | "trigger" | null) => {
            if (type === null) {
                onChange(undefined);
                return;
            }

            const base = { type };

            switch (type) {
                case "multiple":
                    onChange({ ...base, set: [] });
                    break;
                case "iterated":
                    onChange({ ...base, effect: { type: "damage", target: { type: "player" } }, count: 1 });
                    break;
                case "trigger":
                    onChange({
                        ...base,
                        condition: "passive",
                        on: { type: "player" },
                    });
                    break;
                default:
                    // Direct effect
                    onChange({
                        ...base,
                        target: { type: "player" },
                    });
            }
        },
        [onChange]
    );

    const renderEffectFields = () => {
        if (!effect) return null;

        switch (effectType) {
            // Direct effects
            case "give-control":
            case "gain-control":
            case "store":
            case "damage":
            case "ub-damage":
            case "heal":
            case "modify":
            case "draw":
            case "discard":
            case "counter":
            case "add-focus":
            case "create":
            case "tutor":
            case "ritual":
            case "remove-focus":
                // all above cascade into this
                return (
                    <>
                        <Row className="g-2 mb-2">
                            {effectType !== "give-control" &&
                                effectType !== "gain-control" &&
                                effectType !== "counter" &&
                                effectType !== "store" && (
                                    <Col xs={6}>
                                        <Form.Label className="small mb-1">Amount</Form.Label>
                                        <ValueEditor
                                            value={effect.amount}
                                            onChange={(v) => onChange({ ...effect, amount: v })}
                                            disabled={disabled}
                                        />
                                    </Col>
                                )}
                            {(effectType === "modify" || effectType === "store") && (
                                <Col xs={6}>
                                    <Form.Label className="small mb-1">By</Form.Label>
                                    <Form.Control
                                        size="sm"
                                        value={effect.by || ""}
                                        onChange={(e) =>
                                            onChange({ ...effect, by: e.target.value })
                                        }
                                        placeholder="Modifier key"
                                        disabled={disabled}
                                    />
                                </Col>
                            )}
                            {(effectType === "tutor" || effectType === "create") && (
                                <Col xs={6}>
                                    <Form.Label className="small mb-1">Value Key</Form.Label>
                                    <Form.Control
                                        size="sm"
                                        value={effect.value || ""}
                                        onChange={(e) =>
                                            onChange({ ...effect, value: e.target.value })
                                        }
                                        placeholder="Storage key"
                                        disabled={disabled}
                                    />
                                </Col>
                            )}
                        </Row>

                        {effectType !== "ritual" && (<><Form.Label className="small mb-1">Target</Form.Label>
                            <TargetEditor
                                value={effect.target}
                                onChange={(t) => onChange({ ...effect, target: t })}
                                disabled={disabled}
                            /></>)}

                        {(effectType === "give-control") && (
                            <>
                                <Form.Label className="small mb-1 mt-2">
                                    Secondary Target
                                </Form.Label>
                                <TargetEditor
                                    value={effect.secondaryTarget}
                                    onChange={(t) =>
                                        onChange({ ...effect, secondaryTarget: t })
                                    }
                                    disabled={disabled}
                                />
                            </>
                        )}
                    </>
                );

            case "multiple":
                return (
                    <div className="border-start ps-3 ms-2">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <Form.Label className="small mb-0">Effects</Form.Label>
                            <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() =>
                                    onChange({
                                        ...effect,
                                        set: [
                                            ...(effect.set || []),
                                            { type: "damage", target: { type: "player" } },
                                        ],
                                    })
                                }
                                disabled={disabled}
                            >
                                + Add Effect
                            </Button>
                        </div>
                        {(effect.set || []).map((subEffect: any, idx: number) => (
                            <div key={idx} className="mb-2">
                                <div className="d-flex justify-content-between align-items-center">
                                    <Badge bg="secondary">Effect {idx + 1}</Badge>
                                    <Button
                                        size="sm"
                                        variant="outline-danger"
                                        onClick={() =>
                                            onChange({
                                                ...effect,
                                                set: effect.set.filter((_: any, i: number) => i !== idx),
                                            })
                                        }
                                        disabled={disabled}
                                    >
                                        Remove
                                    </Button>
                                </div>
                                <EffectEditor
                                    effect={subEffect}
                                    onChange={(e) =>
                                        onChange({
                                            ...effect,
                                            set: effect.set.map((se: any, i: number) =>
                                                i === idx ? e : se
                                            ),
                                        })
                                    }
                                    disabled={disabled}
                                    depth={depth + 1}
                                />
                            </div>
                        ))}
                    </div>
                );

            case "iterated":
                return (
                    <div className="border-start ps-3 ms-2">
                        <Row className="g-2 mb-2">
                            <Col xs={4}>
                                <Form.Label className="small mb-1">Iterations</Form.Label>
                                <ValueEditor
                                    value={effect.count}
                                    onChange={(v) => onChange({ ...effect, count: v })}
                                    disabled={disabled}
                                />
                            </Col>
                        </Row>
                        <Form.Label className="small mb-1">Effect to Repeat</Form.Label>
                        <EffectEditor
                            effect={effect.effect}
                            onChange={(e) => onChange({ ...effect, effect: e })}
                            disabled={disabled}
                            depth={depth + 1}
                        />
                    </div>
                );

            case "trigger":
                return (
                    <div className="border-start ps-3 ms-2">
                        <Row className="g-2 mb-2">
                            <Col xs={6}>
                                <Form.Label className="small mb-1">Ability Type</Form.Label>
                                <Form.Select
                                    size="sm"
                                    value={effect.source}
                                    onChange={(e) =>
                                        onChange({
                                            ...effect,
                                            source: e.target.value as AbilityTypes,
                                        })
                                    }
                                    disabled={disabled}
                                >
                                    {[...AbilityTypeList, "any", "SYSTEM"].map((t) => (
                                        <option key={t} value={t}>
                                            {t}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col xs={6}>
                                <Form.Label className="small mb-1">Event Type</Form.Label>
                                <Form.Select
                                    size="sm"
                                    value={effect.condition}
                                    onChange={(e) =>
                                        onChange({
                                            ...effect,
                                            condition: e.target.value as TriggerType,
                                        })
                                    }
                                    disabled={disabled}
                                >
                                    {TriggerTypeList.map((t) => (
                                        <option key={t} value={t}>
                                            {t}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                        </Row>
                        <Form.Label className="small mb-1">On Target</Form.Label>
                        <TargetEditor
                            value={effect.on}
                            onChange={(t) => onChange({ ...effect, on: t })}
                            disabled={disabled}
                        />
                        <Form.Check
                            type="checkbox"
                            label="Has modifier effect"
                            checked={!!effect.then}
                            onChange={(e) =>
                                onChange({
                                    ...effect,
                                    then: e.target.checked
                                        ? { type: "modifier", update: "void", effect: 1 }
                                        : undefined,
                                })
                            }
                            disabled={disabled}
                            className="mt-2"
                        />
                        {effect.then && (
                            <>
                                <Form.Label className="small mb-1 mt-2">Then (Modifier)</Form.Label>
                                <div className="border rounded p-2">
                                    <Form.Select
                                        size="sm"
                                        value={effect.then.update}
                                        onChange={(e) =>
                                            onChange({
                                                ...effect,
                                                then: {
                                                    ...effect.then,
                                                    update: e.target.value as "void" | "amount",
                                                },
                                            })
                                        }
                                        disabled={disabled}
                                        className="mb-2"
                                    >
                                        <option value="void">void</option>
                                        <option value="amount">amount</option>
                                    </Form.Select>
                                    {effect.then.update === "amount" && (
                                        <ValueEditor
                                            value={effect.then.effect}
                                            onChange={(v) =>
                                                onChange({
                                                    ...effect,
                                                    then: { ...effect.then, effect: v },
                                                })
                                            }
                                            disabled={disabled}
                                        />
                                    )}
                                </div>
                            </>
                        )}
                        <Form.Check
                            type="checkbox"
                            label="Has 'and' effect"
                            checked={!!effect.and}
                            onChange={(e) =>
                                onChange({
                                    ...effect,
                                    and: e.target.checked
                                        ? { type: "damage", target: { type: "player" } }
                                        : undefined,
                                })
                            }
                            disabled={disabled}
                            className="mt-2"
                        />
                        {effect.and && (
                            <>
                                <Form.Label className="small mb-1">And Effect</Form.Label>
                                <EffectEditor
                                    effect={effect.and}
                                    onChange={(e) => onChange({ ...effect, and: e })}
                                    disabled={disabled}
                                    depth={depth + 1}
                                />
                            </>
                        )}
                    </div>
                );

            default:
                return null;
        }
    };

    const indentClass = depth > 0 ? `ms-${Math.min(depth * 2, 4)}` : "";

    return (
        <div className={`border rounded p-2 mb-2 ${depth > 0 ? "bg-white" : "bg-light"} ${indentClass}`}>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Select
                    size="sm"
                    value={effectType || ""}
                    onChange={(e) =>
                        setEffectType(
                            e.target.value === ""
                                ? null
                                : (e.target.value as EffectType | "multiple" | "iterated" | "trigger")
                        )
                    }
                    disabled={disabled}
                    style={{ maxWidth: "200px" }}
                >
                    <option value="">Select effect type...</option>
                    {!passive && <optgroup label="Direct Effects">
                        {EffectTypeList.map((t) => (
                            <option key={t} value={t}>
                                {t}
                            </option>
                        ))}
                    </optgroup>}
                    <optgroup label="Composite Effects">
                        {!passive && <option value="multiple">multiple</option>}
                        {!passive && <option value="iterated">iterated</option>}
                        {depth === 0 && passive ? <option value="trigger">trigger</option> : null}
                    </optgroup>
                </Form.Select>
                {onRemove && (
                    <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={onRemove}
                        disabled={disabled}
                    >
                        Remove
                    </Button>
                )}
            </div>
            {renderEffectFields()}
        </div>
    );
};

// Main ability editor component
interface AbilityEditorProps {
    passive: boolean;
    value: any;
    onChange: (value: any) => void;
    disabled?: boolean;
    label?: string;
}

const AbilityEditor: React.FC<AbilityEditorProps> = ({
    passive,
    value,
    onChange,
    disabled,
    label,
}) => {
    // Parse string value if needed
    const parsedValue = useMemo(() => {
        if (typeof value === "string") {
            try {
                return JSON.parse(value);
            } catch {
                return null;
            }
        }
        return value;
    }, [value]);

    const handleChange = useCallback(
        (newValue: any) => {
            onChange(newValue);
        },
        [onChange]
    );

    const isEmpty = !parsedValue || (typeof parsedValue === "object" && Object.keys(parsedValue).length === 0);

    return (
        <div>
            {label && <Form.Label className="fw-semibold">{label}</Form.Label>}
            {isEmpty ? (
                <div className="text-center p-3 border rounded bg-light">
                    <p className="text-muted mb-2">No ability defined</p>
                    <Button
                        size="sm"
                        variant="primary"
                        onClick={() =>
                            handleChange({
                                type: "damage",
                                target: { type: "player" },
                            })
                        }
                        disabled={disabled}
                    >
                        Create Ability
                    </Button>
                </div>
            ) : (
                <div className="border rounded p-3 bg-light">
                    <EffectEditor
                        passive={passive}
                        effect={parsedValue}
                        onChange={(e) => handleChange({ ...parsedValue, ...e })}
                        disabled={disabled}
                    />

                    <div className="d-flex justify-content-between mt-3">
                        <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleChange(null)}
                            disabled={disabled}
                        >
                            Clear Ability
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

// Component to replace your existing ability fields
interface CardAbilitiesEditorProps {
    focusedCardData: Record<string, any>;
    dispatchFocusedCardData: (update: Partial<any>) => void;
    saving: boolean;
    abilityFields?: string[];
}

const CardAbilitiesEditor: React.FC<CardAbilitiesEditorProps> = ({
    focusedCardData,
    dispatchFocusedCardData,
    saving,
    abilityFields = ABILITY_FIELDS,
}) => {
    const handleAbilityChange = useCallback(
        (field: string, value: any) => {
            const jsonValue = value === null ? "null" : JSON.stringify(value);
            dispatchFocusedCardData({ [field]: jsonValue } as Partial<any>);
        },
        [dispatchFocusedCardData]
    );

    return (
        <>
            <hr />
            {abilityFields.map((field) => {
                const displayName = field
                    .replace(/_/g, " ")
                    .replace(/ability/gi, "")
                    .trim();

                const currentValue = focusedCardData[field];

                return (
                    <div key={field} className="mb-3">
                        <AbilityEditor
                            passive={field === ABILITY_FIELDS[3]}
                            label={displayName.charAt(0).toUpperCase() + displayName.slice(1)}
                            value={currentValue === "null" ? null : currentValue}
                            onChange={(v) => handleAbilityChange(field, v)}
                            disabled={saving}
                        />
                        {currentValue}
                    </div>
                );
            })}
        </>
    );
};

export default CardAbilitiesEditor;
export { AbilityEditor, EffectEditor, ValueEditor, TargetEditor };